import React from 'react';
import { useNavigate } from 'react-router-dom';
import './HomePage.scss';

interface Feature {
  icon: string;
  title: string;
  description: string;
  delay: number;
}

const FeatureCard: React.FC<{ feature: Feature }> = ({ feature }) => (
  <div className="feature-card">
    <div className="feature-icon">{feature.icon}</div>
    <h3>{feature.title}</h3>
    <p>{feature.description}</p>
  </div>
);

const HomePage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="home-hero">
        <div className="home-hero-content">
          <div className="home-hero-badge">The future of learning</div>

          <h1 className="home-hero-title">
            Own your education.
            <br />
            Create any course.
          </h1>

          <p className="home-hero-description">
            Break free from traditional learning constraints. Create courses on
            any topic, from basic introductions to Ivy League-level depth.
            Access cutting-edge information and build knowledge that evolves
            with you.
          </p>
          {/* Hero Actions */}
          <section className="home-hero-actions-section">
            <div className="home-hero-actions">
              <button
                className="btn-primary"
                onClick={() => navigate('/generate')}
              >
                Start Creating
              </button>
              <button
                className="btn-secondary"
                onClick={() => navigate('/explore')}
              >
                Explore Courses
              </button>
            </div>
          </section>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="use-cases">
        <div className="use-cases-grid">
          <div className="use-case-card">
            <div className="card-visual blockchain"></div>
            <h3>Cryptocurrency & Blockchain</h3>
            <p>Master digital finance and decentralized systems.</p>
          </div>

          <div className="use-case-card featured">
            <div className="card-visual cooking"></div>
            <h3>Molecular Gastronomy</h3>
            <p>The science behind avant-garde culinary techniques.</p>
          </div>

          <div className="use-case-card">
            <div className="card-visual philosophy"></div>
            <h3>Ancient Stoic Philosophy</h3>
            <p>Timeless wisdom from Marcus Aurelius and Seneca.</p>
          </div>

          <div className="use-case-card">
            <div className="card-visual art"></div>
            <h3>Renaissance Art History</h3>
            <p>Explore masterpieces from Da Vinci to Michelangelo.</p>
          </div>

          <div className="use-case-card">
            <div className="card-visual space"></div>
            <h3>Astrophysics & Cosmology</h3>
            <p>Journey through black holes, dark matter, and the universe.</p>
          </div>

          <div className="use-case-card">
            <div className="card-visual psychology"></div>
            <h3>Behavioral Psychology</h3>
            <p>Understand the science behind human decision-making.</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="home-cta">
        <div className="home-cta-container">
          <div className="home-cta-content">
            <div className="cta-main">
              <h2>
                Your curiosity is limitless. Your education should be too.
              </h2>
              <p>
                Join thousands creating personalized courses on any topic
                imaginable.
              </p>

              <div className="cta-action">
                <button
                  className="cta-button-primary"
                  onClick={() => navigate('/generate')}
                >
                  Start Learning for Free
                </button>
                <div className="cta-stats">
                  <div className="stat-item">
                    <span className="stat-number">10K+</span>
                    <span className="stat-label">Courses Created</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number">∞</span>
                    <span className="stat-label">Topics Available</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="cta-visual">
              <div className="learning-elements">
                <div className="course-card">
                  <div className="course-header">
                    <div className="course-icon"></div>
                    <div className="course-title">Physics</div>
                  </div>
                  <div className="progress-bar">
                    <div className="progress-fill"></div>
                  </div>
                </div>
                <div className="course-card secondary">
                  <div className="course-header">
                    <div className="course-icon"></div>
                    <div className="course-title">Art History</div>
                  </div>
                  <div className="progress-bar">
                    <div className="progress-fill"></div>
                  </div>
                </div>
                <div className="floating-topics">
                  <span className="topic-tag">Biology</span>
                  <span className="topic-tag">Cooking</span>
                  <span className="topic-tag">Philosophy</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export { HomePage };
